/* ---------------------------------------------------------------------------
 * MD Fashionwear — wishlist page runtime: first-paint repair + add-to-cart toast
 *
 * Two small jobs, both only on the wishlist page.
 *
 * ---------------------------------------------------------------------------
 * JOB 1 — REPAIR THE FIRST PAINT. Reported 30 Jul 2026, measured, fixed.
 *
 * Symptom: on a normal load the wishlist showed sizes struck through and
 * disabled and other colours unclickable — Sera with no pressable S or L,
 * Imogen with no pressable M/L/XL. It looked like preorder was broken.
 *
 * It was not. md-wishlist-page.js renders on DOMContentLoaded, which on a cold
 * load fires before the saved items and the preorder lookup have settled: the
 * grid came up stale or completely empty and nothing re-ran it. One manual
 * MDWishlistPage.render() produced the correct result every time.
 *
 * So: one repair render after load, plus at most one more if the grid is still
 * empty while handles are saved. Capped by MAX_REPAIRS. Do NOT turn this into
 * a loop and do NOT drive it from a MutationObserver on the grid — an observer
 * that wrote to the DOM it watched froze the browser earlier in this project.
 *
 * ---------------------------------------------------------------------------
 * JOB 2 — THE ADDED TO CART TOAST, AND THE SCROLL BUG IT CAUSED.
 *
 * Add to cart DID add the item — cart went 2 to 3 on the first click — but
 * nothing moved on screen, so it read as a dead button and people clicked
 * twice. A toast confirms it without throwing the drawer over the page.
 *
 * THEN THIS FILE CAUSED A REGRESSION, AND THIS IS THE IMPORTANT PART:
 * it wrapped fetch and fired cart:refresh after EVERY /cart/add.js. The cart
 * drawer also exists on the wishlist page, so adding from the drawer's
 * "You may also love" carousel hit this code too — cart:refresh rebuilds the
 * whole drawer, which threw the shopper back to the top and made it impossible
 * to add several items in a row. That full rebuild is exactly what the cart
 * work deliberately engineered out: drawer adds must update surgically through
 * MDCartSync.afterAdd and the Section Rendering API.
 *
 * THE RULE NOW: this file reacts ONLY to adds that started from a wishlist row
 * button (.mdw-add). Adds from anywhere else — above all the drawer carousel —
 * are left completely alone: no refresh, no toast, no DOM touched. If you ever
 * widen that condition, you will bring the scroll jump back.
 *
 * Toast sizing bugs already fixed, do not regress: it went full-bleed below
 * 699px (a laptop width, not a phone) and rendered see-through because
 * theme.css loads after this file. Hence the 480px breakpoint and !important.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  if (!document.getElementById('md-wishlist-root')) return;
  if (window.__mdWishlistCartReveal) return;
  window.__mdWishlistCartReveal = true;

  /* ==== JOB 1: first-paint repair ====================================== */

  var MAX_REPAIRS = 2;
  var repairs = 0;

  function savedHandles() {
    try {
      var v = JSON.parse(localStorage.getItem('md_wishlist') || '[]');
      return Array.isArray(v) ? v.length : 0;
    } catch (e) { return 0; }
  }

  function gridRows() {
    var g = document.getElementById('md-wishlist-grid');
    return g ? g.querySelectorAll('.mdw-row').length : 0;
  }

  function repaint() {
    if (repairs >= MAX_REPAIRS) return;
    if (!window.MDWishlistPage || typeof window.MDWishlistPage.render !== 'function') return;
    repairs++;
    try { window.MDWishlistPage.render(); } catch (e) { /* never break the page */ }
  }

  function scheduleRepair() {
    setTimeout(repaint, 400);
    setTimeout(function () {
      if (gridRows() === 0 && savedHandles() > 0) repaint();
    }, 1800);
  }

  if (document.readyState === 'complete') scheduleRepair();
  else window.addEventListener('load', scheduleRepair, { once: true });

  /* ==== JOB 2: the toast, wishlist rows only =========================== */

  var native = window.fetch;
  if (typeof native !== 'function') return;

  /* Where did the add come from? Captured on the way down, so it is recorded
     before the renderer's own handler runs.
       'row'   — a wishlist row's Add to cart. The only case we react to.
       'buy'   — Buy it now / Buy all now: adds then leaves for checkout, so a
                 toast would flash for an instant on the way out.
       'other' — anything else, above all the drawer carousel. Hands off. */
  var origin = 'other';
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('.mdw-buy, #md-wishlist-buy-all')) { origin = 'buy'; return; }
    if (e.target.closest('.mdw-add')) { origin = 'row'; return; }
    origin = 'other';
  }, true);

  function isAddCall(input) {
    var url = '';
    if (typeof input === 'string') url = input;
    else if (input && input.url) url = input.url;
    return url.indexOf('/cart/add') > -1;
  }

  var CSS =
    '.md-atc-toast{position:fixed !important;z-index:2147483000;right:20px;bottom:24px;' +
      'left:auto !important;display:flex !important;align-items:center;gap:14px;' +
      'box-sizing:border-box;width:340px !important;max-width:calc(100vw - 40px) !important;' +
      'padding:12px 18px 12px 12px;border-radius:16px;' +
      'background:#232323 !important;background-color:#232323 !important;' +
      'color:#fff !important;backdrop-filter:none !important;' +
      'box-shadow:0 18px 40px rgba(0,0,0,.32);opacity:0;transform:translateY(10px);' +
      'transition:opacity .22s ease,transform .22s ease;pointer-events:none}' +
    '.md-atc-toast.is-in{opacity:1 !important;transform:translateY(0)}' +
    '.md-atc-toast__img{flex:0 0 auto;width:56px;height:66px;border-radius:10px;' +
      'object-fit:cover;background:#3a3a3a;display:block}' +
    '.md-atc-toast__body{min-width:0;flex:1 1 auto}' +
    '.md-atc-toast__tag{display:block !important;font-size:11px;letter-spacing:.14em;' +
      'line-height:1.2;text-transform:uppercase;color:#7fc79a !important;margin:0 0 5px}' +
    '.md-atc-toast__title{display:block !important;font-size:15px;line-height:1.25;' +
      'color:#fff !important;margin:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.md-atc-toast__opt{display:block !important;font-size:13px;line-height:1.3;' +
      'color:#9a9a9a !important;margin:3px 0 0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '@media (max-width:480px){.md-atc-toast{right:12px;bottom:88px;' +
      'width:calc(100vw - 24px) !important;max-width:calc(100vw - 24px) !important}}' +
    '@media (prefers-reduced-motion:reduce){.md-atc-toast{transition:none}}';

  var styled = false;
  function ensureStyle() {
    if (styled) return;
    styled = true;
    var s = document.createElement('style');
    s.setAttribute('data-md-atc', '1');
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  var node = null;
  var hideTimer = null;

  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function toast(item) {
    ensureStyle();

    if (!node) {
      node = document.createElement('div');
      node.className = 'md-atc-toast';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      node.innerHTML =
        '<img class="md-atc-toast__img" alt="">' +
        '<div class="md-atc-toast__body">' +
          '<span class="md-atc-toast__tag">' + mdT('md_wishlist_cart_reveal.added_to_cart', 'Added to cart') + '</span>' +
          '<span class="md-atc-toast__title"></span>' +
          '<span class="md-atc-toast__opt"></span>' +
        '</div>';
      document.body.appendChild(node);
    }

    if (document.body.lastElementChild !== node) document.body.appendChild(node);

    var img = node.querySelector('.md-atc-toast__img');
    var src = item.image || item.featured_image || '';
    if (src && typeof src === 'object') src = src.url || src.src || '';
    if (src) { img.src = sized(src, 140); img.style.visibility = ''; }
    else { img.removeAttribute('src'); img.style.visibility = 'hidden'; }

    node.querySelector('.md-atc-toast__title').textContent =
      item.product_title || item.title || '';

    var opt = item.variant_title || '';
    if (/default title/i.test(opt)) opt = '';
    var optEl = node.querySelector('.md-atc-toast__opt');
    optEl.textContent = opt;
    optEl.style.display = opt ? '' : 'none';

    node.classList.remove('is-in');
    void node.offsetWidth;
    node.classList.add('is-in');

    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      if (node) node.classList.remove('is-in');
    }, 3600);
  }

  /* Keeps the header count honest. Only ever called for a wishlist-row add —
     firing it for a drawer add rebuilds the drawer and scrolls the page. */
  function quietRefresh() {
    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
    } catch (e) { /* the count updates on the next load */ }
  }

  window.fetch = function (input, init) {
    var p = native.apply(this, arguments);
    if (!isAddCall(input)) return p;

    /* Read it now: another click can change origin before this resolves. */
    var from = origin;

    p.then(function (res) {
      if (!res || !res.ok) return;
      if (from !== 'row') return;      // drawer carousel and Buy it now: untouched

      quietRefresh();

      return res.clone().json().then(function (data) {
        var item = data;
        if (data && data.items && data.items.length) item = data.items[0];
        if (item) toast(item);
      }).catch(function () {
        toast({ product_title: mdT('md_wishlist_cart_reveal.added_to_your_cart', 'Added to your cart') });
      });
    }).catch(function () {});

    return p;
  };
})();
